import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'

import { useGetTaxRatesQuery } from '@/app/services/taxRates'
import { selectUserRole, setAppBarState } from '@/app/slices/appSlice'
import {
    selectLastPaymentStatus,
    selectOrganisation,
    selectOrganisationId,
    setDefaultTaxRateId,
} from '@/app/slices/organisationSlice'
import { useAuth } from '@/common/hooks/useAuth'
import { Paths, showResourceCentre } from '@/common/utils'
import { isOnAdvancedPlan } from '@/common/utils/subscriptionUtils'

const pathsToHideAppBar = [
    Paths.SIGNOUT_PATHNAME,
    Paths.ERROR_PATHNAME,
    Paths.CUSTOMER_FORMATTED_QUOTE_PATHNAME,
    Paths.SHARED_PATHNAME,
]

const pathsToHideAllRegex = [
    new RegExp(`^${Paths.CUSTOMER_FORMATTED_QUOTE_PATHNAME}/`, 'g'),
    new RegExp(`^${Paths.SHARED_PATHNAME}/`, 'g'),
    new RegExp('^/projects/[0-9a-fA-F-]+/documents/[\\w-]+', 'g'),
    new RegExp('^/quotes/[0-9a-fA-F-]+/documents/[\\w-]+', 'g'),
]

const pathsToHideMyAccount = [Paths.WELCOME_PATHNAME, Paths.CUSTOMER_FORMATTED_QUOTE_PATHNAME, Paths.SHARED_PATHNAME]

const pathsToHideSubscription = [
    Paths.ACTIVATE_PATHNAME,
    Paths.ACTIVATE_BILLING_DETAILS_PATHNAME,
    Paths.ACTIVATE_PAYMENT_DETAILS_PATHNAME,
    Paths.NO_ACTIVE_SUBSCRIPTION_PATHNAME,
    Paths.ERROR_PATHNAME,
    Paths.WELCOME_PATHNAME,
    Paths.CUSTOMER_FORMATTED_QUOTE_PATHNAME,
    Paths.SHARED_PATHNAME,
]

/**
 * UserObserverContainer - Observer wrapper that manages AppBar state based on auth and route.
 *
 * Responsibilities:
 * - Updates AppBar visibility based on authentication status and current route
 * - Manages subscription/trial bars visibility
 * - Sets default tax rate from organization data
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {React.ReactNode} Children unchanged (observer only)
 */
const UserObserverContainer = ({ children }) => {
    const location = useLocation()
    const dispatch = useDispatch()

    const organisation = useSelector(selectOrganisation)
    const organisationId = useSelector(selectOrganisationId)
    const lastPaymentStatus = useSelector(selectLastPaymentStatus)
    const userRole = useSelector(selectUserRole)

    // Unified auth hook - supports both MSAL and Kinde providers
    const { isAuthenticated: userIsAuthenticated } = useAuth()

    const { data: taxRates } = useGetTaxRatesQuery({ organisationId }, { skip: !organisationId })

    // Note: Favicon/title updates handled by useFaviconUpdater() in parent Layout component

    useEffect(() => {
        const hideAll = pathsToHideAllRegex.some((path) => location.pathname.match(path))

        dispatch(
            setAppBarState({
                menuHidden:
                    userIsAuthenticated &&
                    !!organisation &&
                    !organisation.hasActiveSubscription &&
                    (!organisation.trial || organisation.trial.hasExpired) &&
                    !organisation.freePlan,
                hidden: !userIsAuthenticated || hideAll || pathsToHideAppBar.includes(location.pathname),
                myAccountHidden: pathsToHideMyAccount.includes(location.pathname),
                showUpdatePaymentBar: lastPaymentStatus === 'failed' && userRole === 'owner',
                showSubscriptionBar:
                    organisation &&
                    !organisation.hasActiveSubscription &&
                    !pathsToHideSubscription.includes(location.pathname) &&
                    !organisation.freePlan,
                showTrialWithSubscriptionBar:
                    organisation &&
                    organisation.trial &&
                    !organisation.trial?.hasExpired &&
                    organisation.hasActiveSubscription &&
                    !isOnAdvancedPlan(organisation) &&
                    !pathsToHideSubscription.includes(location.pathname) &&
                    !organisation.freePlan,
                freePlan: organisation?.freePlan,
                title: '',
                toolbarItems: [],
            })
        )
        showResourceCentre(!hideAll)
    }, [location, organisation, userIsAuthenticated, lastPaymentStatus, userRole, dispatch])

    useEffect(() => {
        dispatch(setDefaultTaxRateId(taxRates?.find((taxRate) => taxRate.isDefault)?.id || null))
    }, [taxRates, dispatch])

    return children
}

UserObserverContainer.propTypes = {
    children: PropTypes.node.isRequired,
}

export default UserObserverContainer
